import React, { useState, useEffect, useCallback } from 'react';
import { Copy, ShieldCheck, Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { getUserDuplicates, removeUserDuplicates } from '../services/api';
import { toast } from 'sonner';

const roleLabels = { admin: 'Administrador', gestor: 'Gestor', usuario: 'Usuário' };
const reasonLabels = { email: 'Mesmo e-mail', nome: 'Mesmo nome', vinculado: 'Vinculado' };

const DuplicateUsersDialog = ({ open, onOpenChange, onRemoved }) => {
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setConfirm(false);
    try {
      const res = await getUserDuplicates();
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao buscar duplicados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setData(null);
      load();
    }
  }, [open, load]);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const res = await removeUserDuplicates();
      toast.success(`${res.data.removed_count} usuário(s) duplicado(s) removido(s)`);
      onRemoved?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover duplicados');
    } finally {
      setRemoving(false);
    }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d || '-'; }
  };

  const removable = data?.total_removable || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-users-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <Copy className="w-5 h-5 text-[#003366]" />
            Localizar e remover duplicados
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Considera duplicado quem tem o <strong>mesmo e-mail</strong> (ignorando maiúsculas/espaços) ou o <strong>mesmo nome</strong>. Em cada grupo, o usuário <strong>mais antigo é mantido</strong> e os demais são removidos.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex flex-col items-center justify-center text-gray-500" data-testid="dup-loading">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <p className="text-sm">Analisando usuários...</p>
          </div>
        ) : !data ? null : removable === 0 ? (
          <div className="py-10 flex flex-col items-center justify-center text-emerald-600" data-testid="dup-none">
            <CheckCircle2 className="w-10 h-10 mb-2" />
            <p className="font-semibold">Nenhum duplicado encontrado</p>
            <p className="text-sm text-gray-500">{data.total_users} usuários analisados</p>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="dup-result">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="dup-summary">
                {data.group_count} grupo(s) · {removable} usuário(s) a remover
              </Badge>
              <span className="text-xs text-gray-500">de {data.total_users} usuários</span>
            </div>

            <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
              {data.groups.map((g, gi) => (
                <div key={gi} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`dup-group-${gi}`}>
                  <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 border-b border-gray-200">
                    {g.reasons.map((r) => (
                      <Badge key={r} variant="outline" className="bg-blue-50 text-[#003366] border-blue-200 text-xs">
                        {reasonLabels[r] || r}
                      </Badge>
                    ))}
                  </div>
                  <div className="divide-y divide-gray-100">
                    <div className="flex items-center justify-between px-3 py-2 bg-emerald-50/40">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{g.kept.name}</p>
                          <p className="text-xs text-gray-500">{g.kept.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Manter</Badge>
                        <p className="text-xs text-gray-400 mt-1">Criado {formatDate(g.kept.created_at)}</p>
                      </div>
                    </div>
                    {g.remove.map((m) => (
                      <div key={m.id} className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Trash2 className="w-4 h-4 text-rose-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">{m.name}</p>
                            <p className="text-xs text-gray-500">{m.email} · {roleLabels[m.role] || m.role}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-xs">Remover</Badge>
                          <p className="text-xs text-gray-400 mt-1">Criado {formatDate(m.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {confirm && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="dup-confirm-warning">
                Esta ação é <strong>irreversível</strong>. {removable} usuário(s) serão excluídos permanentemente e suas sessões encerradas. Deseja continuar?
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-white border-gray-300 text-gray-700" data-testid="dup-close-btn">
            Fechar
          </Button>
          {removable > 0 && !confirm && (
            <Button onClick={() => setConfirm(true)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="dup-remove-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Remover {removable} duplicado(s)
            </Button>
          )}
          {removable > 0 && confirm && (
            <Button onClick={handleRemove} disabled={removing} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="dup-confirm-remove-btn">
              {removing ? 'Removendo...' : 'Sim, remover definitivamente'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateUsersDialog;
